import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2, Plus, Search, Filter, MoreVertical, Edit, Trash2, 
  Eye, Users, Plane, Shield, Award, Globe, Phone, Mail, RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const ORG_TYPES = [
  { value: 'operator', label: 'Operator (AOC)', icon: Plane },
  { value: 'owner', label: 'Owner', icon: Building2 },
  { value: 'camo', label: 'CAMO', icon: Shield },
  { value: 'mro', label: 'MRO (Part-145)', icon: Award },
  { value: 'airport', label: 'Airport', icon: Building2 },
  { value: 'ansp', label: 'ANSP', icon: Globe },
  { value: 'leasing_company', label: 'Leasing Company', icon: Building2 },
  { value: 'training_organization', label: 'Training Org', icon: Users },
  { value: 'ground_handler', label: 'Ground Handler', icon: Users },
];

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  suspended: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  revoked: 'bg-red-500/20 text-red-400 border-red-500/30',
  pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const CERTIFICATE_TYPES = [
  { value: 'aoc', label: 'Air Operator Certificate (AOC)' },
  { value: 'camo_approval', label: 'CAMO Approval' },
  { value: 'part_145', label: 'Part-145 Approval' },
  { value: 'gacar_145', label: 'GACAR-145 Approval' },
  { value: 'ato', label: 'Approved Training Organization' },
  { value: 'aerodrome_certificate', label: 'Aerodrome Certificate' },
];

export default function OrganizationsPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { isDark } = useTheme();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAircraftDialog, setShowAircraftDialog] = useState(false);
  const [linkedAircraft, setLinkedAircraft] = useState([]);
  const [loadingAircraft, setLoadingAircraft] = useState(false);
  const [formData, setFormData] = useState({
    legal_name: '',
    legal_name_ar: '',
    trading_name: '',
    organization_types: [],
    country: '',
    primary_base: '',
    regulatory_authority: '',
    accountable_manager: '',
    accountable_manager_email: '',
    safety_manager: '',
    safety_manager_email: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    certificates: []
  });

  useEffect(() => {
    fetchOrganizations();
    fetchStats();
  }, [api]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterType !== 'all') params.append('org_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await api.get(`/organizations?${params.toString()}`);
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Failed to fetch organizations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/organizations/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      if (!formData.legal_name || formData.organization_types.length === 0) {
        toast.error('Please fill in required fields');
        return;
      }
      
      if (editMode && editingId) {
        await api.put(`/organizations/${editingId}`, formData);
        toast.success('Organization updated successfully');
      } else {
        await api.post('/organizations', formData);
        toast.success('Organization created successfully');
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchOrganizations();
      fetchStats();
    } catch (error) {
      toast.error(`Failed to ${editMode ? 'update' : 'create'} organization`);
      console.error(error);
    }
  };

  const handleEditOrganization = async (org) => {
    try {
      const response = await api.get(`/organizations/${org.id}`);
      const data = response.data;
      
      setFormData({
        legal_name: data.legal_name || '',
        legal_name_ar: data.legal_name_ar || '',
        trading_name: data.trading_name || '',
        organization_types: data.organization_types || [],
        country: data.country || '',
        primary_base: data.primary_base || '',
        regulatory_authority: data.regulatory_authority || '',
        accountable_manager: data.accountable_manager || '',
        accountable_manager_email: data.accountable_manager_email || '',
        safety_manager: data.safety_manager || '',
        safety_manager_email: data.safety_manager_email || '',
        address: data.address || '',
        city: data.city || '',
        phone: data.phone || '',
        email: data.email || '',
        certificates: data.certificates || []
      });
      
      setEditMode(true);
      setEditingId(org.id);
      setShowCreateDialog(true);
    } catch (error) {
      toast.error('Failed to load organization details');
      console.error(error);
    }
  };

  const handleViewAircraft = async (org) => {
    try {
      setLoadingAircraft(true);
      setSelectedOrg(org);
      
      // Fetch aircraft linked to this organization (as operator, owner, CAMO, or maintenance provider)
      const response = await api.get(`/aircraft?limit=200`);
      const allAircraft = response.data;
      
      // Filter aircraft linked to this organization
      const linked = allAircraft.filter(ac => 
        ac.operator_id === org.id || 
        ac.owner_id === org.id || 
        ac.camo_id === org.id || 
        ac.maintenance_provider_id === org.id
      );
      
      setLinkedAircraft(linked);
      setShowAircraftDialog(true);
    } catch (error) {
      toast.error('Failed to fetch linked aircraft');
      console.error(error);
    } finally {
      setLoadingAircraft(false);
    }
  };

  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) return;
    
    try {
      await api.delete(`/organizations/${orgId}`);
      toast.success('Organization deleted');
      fetchOrganizations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete organization');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      legal_name: '',
      legal_name_ar: '',
      trading_name: '',
      organization_types: [],
      country: '',
      primary_base: '',
      regulatory_authority: '',
      accountable_manager: '',
      accountable_manager_email: '',
      safety_manager: '',
      safety_manager_email: '',
      address: '',
      city: '',
      phone: '',
      email: '',
      certificates: []
    });
    setEditMode(false);
    setEditingId(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const addCertificate = () => {
    setFormData(prev => ({
      ...prev,
      certificates: [...prev.certificates, {
        certificate_type: '',
        certificate_number: '',
        regulatory_authority: '',
        issue_date: '',
        expiry_date: '',
        approval_scope: ''
      }]
    }));
  };

  const updateCertificate = (index, field, value) => {
    setFormData(prev => {
      const certs = [...prev.certificates];
      certs[index] = { ...certs[index], [field]: value };
      return { ...prev, certificates: certs };
    });
  };

  const removeCertificate = (index) => {
    setFormData(prev => ({
      ...prev,
      certificates: prev.certificates.filter((_, i) => i !== index)
    }));
  };

  const viewOrgDetails = async (org) => {
    try {
      const response = await api.get(`/organizations/${org.id}`);
      setSelectedOrg(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to fetch organization details');
    }
  };

  const filteredOrgs = organizations.filter(org => {
    if (searchQuery && !org.legal_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !org.trading_name?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
            Organizations
          </h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
            Manage operators, owners, CAMOs, MROs and other aviation organizations
          </p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2" data-testid="create-org-btn">
          <Plus className="w-4 h-4" />
          Add Organization
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                {stats.total || 0}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Total Organizations
              </div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">
                {stats.by_status?.active || 0}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Active
              </div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-sky-500">
                {stats.by_type?.operator || 0}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Operators
              </div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-500">
                {stats.by_type?.mro || 0}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                MROs
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="org-search"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]" data-testid="filter-type">
                <SelectValue placeholder="Organization Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ORG_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchOrganizations} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Organizations Table */}
      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? "border-slate-800" : ""}>
                <TableHead className={isDark ? "text-slate-400" : ""}>Organization</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Type</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Country</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Regulator</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Status</TableHead>
                <TableHead className={cn("w-[50px]", isDark ? "text-slate-400" : "")}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                      No organizations found
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrgs.map(org => (
                  <TableRow 
                    key={org.id} 
                    className={cn("cursor-pointer", isDark ? "border-slate-800 hover:bg-slate-800/50" : "hover:bg-gray-50")}
                    onClick={() => viewOrgDetails(org)}
                    data-testid={`org-row-${org.id}`}
                  >
                    <TableCell>
                      <div>
                        <div className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                          {org.legal_name}
                        </div>
                        {org.trading_name && (
                          <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            {org.trading_name}
                          </div>
                        )}
                        <div className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>
                          {org.org_number}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {org.organization_types?.map(type => (
                          <Badge key={type} variant="outline" className="text-xs">
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className={isDark ? "text-slate-300" : "text-gray-700"}>
                      {org.country}
                    </TableCell>
                    <TableCell className={isDark ? "text-slate-300" : "text-gray-700"}>
                      {org.regulatory_authority || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[org.status] || STATUS_COLORS.active}>
                        {org.status}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewOrgDetails(org)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditOrganization(org)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Organization
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewAircraft(org)}>
                            <Plane className="w-4 h-4 mr-2" />
                            View Aircraft
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-500"
                            onClick={() => handleDeleteOrganization(org.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Organization Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className={cn("max-w-2xl max-h-[90vh] overflow-y-auto", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>
              {editMode ? 'Edit Organization' : 'Add Organization'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="certificates">Certificates</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Legal Name *</Label>
                  <Input
                    value={formData.legal_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
                    placeholder="Full legal name"
                    data-testid="org-legal-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Legal Name (Arabic)</Label>
                  <Input
                    value={formData.legal_name_ar}
                    onChange={(e) => setFormData(prev => ({ ...prev, legal_name_ar: e.target.value }))}
                    placeholder="الاسم القانوني"
                    dir="rtl"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Trading Name</Label>
                  <Input
                    value={formData.trading_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, trading_name: e.target.value }))}
                    placeholder="Brand/trading name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Country *</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="ISO code (e.g., SA)"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Organization Types *</Label>
                <div className="flex flex-wrap gap-2">
                  {ORG_TYPES.map(type => (
                    <Badge
                      key={type.value}
                      variant={formData.organization_types.includes(type.value) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          organization_types: prev.organization_types.includes(type.value)
                            ? prev.organization_types.filter(t => t !== type.value)
                            : [...prev.organization_types, type.value]
                        }));
                      }}
                    >
                      {type.label}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Primary Base (ICAO)</Label>
                  <Input
                    value={formData.primary_base}
                    onChange={(e) => setFormData(prev => ({ ...prev, primary_base: e.target.value }))}
                    placeholder="e.g., OERK"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Regulatory Authority</Label>
                  <Select 
                    value={formData.regulatory_authority} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, regulatory_authority: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select authority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GACA">GACA (Saudi Arabia)</SelectItem>
                      <SelectItem value="EASA">EASA (European Union)</SelectItem>
                      <SelectItem value="FAA">FAA (United States)</SelectItem>
                      <SelectItem value="GCAA">GCAA (UAE)</SelectItem>
                      <SelectItem value="ICAO">ICAO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="contacts" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Accountable Manager</Label>
                  <Input
                    value={formData.accountable_manager}
                    onChange={(e) => setFormData(prev => ({ ...prev, accountable_manager: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>AM Email</Label>
                  <Input
                    type="email"
                    value={formData.accountable_manager_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, accountable_manager_email: e.target.value }))}
                    placeholder="am@company.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Safety Manager</Label>
                  <Input
                    value={formData.safety_manager}
                    onChange={(e) => setFormData(prev => ({ ...prev, safety_manager: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>SM Email</Label>
                  <Input
                    type="email"
                    value={formData.safety_manager_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, safety_manager_email: e.target.value }))}
                    placeholder="sm@company.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Street address"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+966..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="certificates" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <Label className={isDark ? "text-slate-300" : ""}>Certificates & Approvals</Label>
                <Button variant="outline" size="sm" onClick={addCertificate}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Certificate
                </Button>
              </div>
              
              {formData.certificates.length === 0 ? (
                <div className={cn("text-center py-8 text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                  No certificates added. Click "Add Certificate" to add one.
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.certificates.map((cert, index) => (
                    <Card key={index} className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <span className={cn("text-sm font-medium", isDark ? "text-slate-300" : "")}>
                            Certificate #{index + 1}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeCertificate(index)}
                            className="text-red-500 h-6 px-2"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Select 
                            value={cert.certificate_type} 
                            onValueChange={(v) => updateCertificate(index, 'certificate_type', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Certificate type" />
                            </SelectTrigger>
                            <SelectContent>
                              {CERTIFICATE_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={cert.certificate_number}
                            onChange={(e) => updateCertificate(index, 'certificate_number', e.target.value)}
                            placeholder="Certificate number"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <Input
                            value={cert.regulatory_authority}
                            onChange={(e) => updateCertificate(index, 'regulatory_authority', e.target.value)}
                            placeholder="Authority"
                          />
                          <Input
                            type="date"
                            value={cert.issue_date}
                            onChange={(e) => updateCertificate(index, 'issue_date', e.target.value)}
                            placeholder="Issue date"
                          />
                          <Input
                            type="date"
                            value={cert.expiry_date}
                            onChange={(e) => updateCertificate(index, 'expiry_date', e.target.value)}
                            placeholder="Expiry date"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrganization} data-testid="save-org-btn">
              {editMode ? 'Update Organization' : 'Create Organization'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Linked Aircraft Dialog */}
      <Dialog open={showAircraftDialog} onOpenChange={setShowAircraftDialog}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>
              Aircraft Linked to {selectedOrg?.legal_name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingAircraft ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-sky-500" />
            </div>
          ) : linkedAircraft.length === 0 ? (
            <div className={cn("text-center py-8", isDark ? "text-slate-400" : "text-gray-500")}>
              <Plane className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No aircraft linked to this organization</p>
              <p className="text-sm mt-2">
                Aircraft can be linked by setting this organization as Owner, Operator, CAMO, or Maintenance Provider in the Aircraft page.
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {linkedAircraft.map(ac => (
                <div 
                  key={ac.id} 
                  className={cn(
                    "p-3 rounded-lg border flex items-center justify-between",
                    isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      isDark ? "bg-sky-600/20" : "bg-sky-100"
                    )}>
                      <Plane className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <div className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {ac.registration}
                      </div>
                      <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                        {ac.aircraft_type} • {ac.manufacturer} {ac.model}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {ac.operator_id === selectedOrg?.id && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Operator</Badge>
                    )}
                    {ac.owner_id === selectedOrg?.id && (
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Owner</Badge>
                    )}
                    {ac.camo_id === selectedOrg?.id && (
                      <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">CAMO</Badge>
                    )}
                    {ac.maintenance_provider_id === selectedOrg?.id && (
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">MRO</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Organization Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>
              {selectedOrg?.legal_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrg && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {selectedOrg.organization_types?.map(type => (
                  <Badge key={type} variant="outline">{type}</Badge>
                ))}
                <Badge className={STATUS_COLORS[selectedOrg.status]}>
                  {selectedOrg.status}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Trading Name</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedOrg.trading_name || '-'}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Country</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedOrg.country}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Primary Base</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedOrg.primary_base || '-'}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Regulatory Authority</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedOrg.regulatory_authority || '-'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Key Personnel</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <p className={cn("text-sm font-medium", isDark ? "text-white" : "")}>
                      Accountable Manager
                    </p>
                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                      {selectedOrg.accountable_manager || '-'}
                    </p>
                  </div>
                  <div>
                    <p className={cn("text-sm font-medium", isDark ? "text-white" : "")}>
                      Safety Manager
                    </p>
                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                      {selectedOrg.safety_manager || '-'}
                    </p>
                  </div>
                </div>
              </div>
              
              {selectedOrg.certificates?.length > 0 && (
                <div className="border-t pt-4">
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Certificates</Label>
                  <div className="space-y-2 mt-2">
                    {selectedOrg.certificates.map((cert, i) => (
                      <div key={i} className={cn("p-2 rounded text-sm", isDark ? "bg-slate-800" : "bg-gray-50")}>
                        <div className="flex justify-between">
                          <span className="font-medium">{cert.certificate_type}</span>
                          <span>{cert.certificate_number}</span>
                        </div>
                        <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                          Expires: {cert.expiry_date || 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
